import { Link } from "react-router";
import { chaptersFromDoc, countdownLabel, journeyRoute, selectNextAction, tripCalendarDays, tripNightCount } from "@shared/itinerary.ts";
import { eachDateInclusive, formatDateLabel, tripPhase } from "@shared/time.ts";
import { useTrip, useTripClock } from "../../app/trip.tsx";
import { useSession } from "../../app/session.tsx";
import { Photo } from "../../components/Photo.tsx";
import { DateStrip } from "../../components/DateStrip.tsx";
import { AlertSetupCard } from "../updates/AlertSetup.tsx";

export function JourneyPage() {
  const { trip, loading, empty, devicePreviewNow } = useTrip();
  const { role } = useSession();
  const now = useTripClock();
  if (loading && !trip) {
    return (
      <main className="page">
        <div className="skeleton" style={{ height: 220 }} />
      </main>
    );
  }
  if (empty || !trip) {
    return (
      <main className="page">
        <h1 className="page-title">The journey is waiting for an import</h1>
        <p className="muted">The owner can upload the reviewed seed from Edit.</p>
        <Link className="btn" to="/editor">Open editor</Link>
      </main>
    );
  }

  const phase = tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone);
  const chapters = chaptersFromDoc(trip);
  const nights = tripNightCount(trip);
  const days = tripCalendarDays(trip);
  const route = journeyRoute(trip);
  const next = selectNextAction(trip, now);
  const allDates = eachDateInclusive(trip.trip.startDate, trip.trip.endDate);

  return (
    <main className="page journey-page">
      <header className="topbar">
        <div>
          <p className="wordmark">Thailand, together</p>
          <p className="muted">{trip.trip.subtitle} · October-November 2026</p>
        </div>
      </header>

      {devicePreviewNow && role === "owner" ? <p className="banner warn">Preview clock on this phone only: {devicePreviewNow}</p> : null}

      <section className="hero">
        <Photo assetId="journey" alt="Thailand destination photograph for the journey cover" priority height={260} />
        <div className="hero-copy">
          <p className="kicker">{phase === "before" ? countdownLabel(now, trip.trip.startDate, trip.trip.homeTimezone) : phase === "during" ? "On the journey" : "The record"}</p>
          <h1 className="display-title">Four places, one itinerary</h1>
          <p>{days} calendar days · {nights} hotel nights</p>
        </div>
      </section>

      <AlertSetupCard />

      <nav className="route-strip" aria-label="Route" style={{ marginTop: 20 }}>
        {route.map((stop) => (
          <Link key={stop.id} className={stop.kind === "travel" ? "travel" : ""} to={stop.href}>
            <span className="kicker">{stop.kind === "travel" ? "Travel" : "Stay"}</span>
            {stop.label}
            <span className="muted">{formatDateLabel(stop.startDate)}</span>
          </Link>
        ))}
      </nav>

      <section className="card" style={{ marginTop: 16, background: "var(--sage)", border: 0 }}>
        <p className="kicker">{next.kicker}</p>
        <h2 className="serif" style={{ margin: "6px 0" }}>{next.title}</h2>
        {next.place ? <p className="muted">{next.place}</p> : null}
        <Link className="btn" to={next.primaryHref}>{next.primaryLabel}</Link>
      </section>

      <p className="row-actions" style={{ marginTop: 16 }}>
        <Link className="btn-secondary" to="/map">Open the map</Link>
      </p>

      <h2 className="section-title">Days</h2>
      <DateStrip dates={allDates} hrefFor={(date) => `/journey/day/${date}`} />

      <div className="chapter-grid" style={{ marginTop: 24 }}>
        {chapters.map((chapter, index) => (
          <article key={chapter.id} className="card chapter-card">
            <Photo assetId={chapter.imageAssetId} alt={`${chapter.name} destination photograph, not a booked room`} />
            <div style={{ padding: 16 }}>
              <p className="kicker">Chapter {index + 1}</p>
              <h2 className="serif" style={{ margin: "0 0 6px" }}>{chapter.name}</h2>
              <p className="muted">{formatDateLabel(chapter.startDate)} - {formatDateLabel(chapter.endDate)} · {chapter.nights} {chapter.nights === 1 ? "night" : "nights"}</p>
              <p>{chapter.summary}</p>
              <div className="row-actions">
                <Link className="btn" to={`/journey/day/${chapter.startDate}`}>Open this chapter</Link>
                <Link className="btn-secondary" to={`/journey/stay/${chapter.id}`}>Stay details</Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
